import {json, texture} from "p3";
import common from "../common";
import settings from "../settings";
import {locale, resizeLabelToFitText} from "../utils";
import GameScene from "./GameScene";
import MenuScene from "./MenuScene";

export default class InventoryScene extends MenuScene {
    /**
     * @param {Inventory} inventory
     */
    constructor(inventory) {
        super("inventory");

        this._inventory = inventory;

        this._fill = null;
        this._closeButton = null;

        this._bag = null;
        this._panel = null;
        this._coinHolder = null;

        this._items = [];

        this._scrolling = false;
        this._scrollPos = 0;
        this._scrollMax = 0;
        this._scrollDistance = 0;
    }

    init() {
        super.init();
    }

    onSceneLoaded() {
        super.onSceneLoaded();

        this.interactive = true;

        if (!common.saveData.settings.musicMute) {
            P3.audio.playMusic("music_map_loop");
        }

        this.createFill();
        this.createTitleBanner(locale('ui_inventory_title'));

        this._bag = new PIXI.Sprite(texture("inventory_bag"));
        this._bag.anchor.set(0.5, 1);
        this._bag.x = common.STAGE_WIDTH/2;
        this._bag.y = common.STAGE_HEIGHT;
        this.addChild(this._bag);

        this._panel = new PIXI.Sprite(texture("inventory_panel"));
        this._panel.anchor.set(0.5, 0.5);
        this._panel.x = common.STAGE_WIDTH/2;
        this._panel.y = (common.STAGE_HEIGHT/2)+60;
        this.addChild(this._panel);

        this._itemHolder = new PIXI.Container();
        this._panel.addChild(this._itemHolder);

        this._coinHolder = new PIXI.Sprite(texture("panel_coins"));
        this._coinHolder.anchor.set(0.5);
        this._coinHolder.x = (common.STAGE_WIDTH / 2) - 10;
        this._coinHolder.y = this._titleBanner.y + 50;
        this.addChild(this._coinHolder);
        
        const coin = new PIXI.Sprite(texture("icon_coin"));
        coin.anchor.set(0.5);
        coin.y = -2;
        this._coinHolder.addChild(coin);
        
        const coinLabel = new PIXI.BitmapText(String(Math.min(999, common.saveData.activeSlot.coins)), {
            fontName: "Alghera Pro",
            fontSize: 32,
            align: "left",
            tint: 0x000000
        });
        coinLabel.anchor.set(0, 0.5);
        coinLabel.x = 17;
        this._coinHolder.addChild(coinLabel);

        this._closeButton = this.addCloseButton(this._panel.x + (this._panel.width/2), this._panel.y - (this._panel.height/2));
        this._closeButton.overSoundName = "sfx_btn_generic_rollover";
        this._closeButton.clickSoundName = "sfx_btn_bag_close";

        this._downButton = P3.Button.create(
            texture("btn_small_out"),
            texture("btn_small_out")
        );
        this._downButton.x = this._panel.x + (this._panel.width/2);
        this._downButton.y = this._panel.y + 100;
        this._downButton.signals.click.add(() => {
            this.scroll(1);
        }, this);
        this.addChild(this._downButton);

        this._upButton = P3.Button.create(
            texture("btn_small_out"),
            texture("btn_small_out")
        );
        this._upButton.x = this._panel.x + (this._panel.width/2);
        this._upButton.y = this._panel.y;
        this._upButton.signals.click.add(() => {
            this.scroll(-1);
        }, this);
        this.addChild(this._upButton);

        this.fillInventory();

        this.resize();

        this.animateShow();
    }

    fillInventory()
    {
        const boxSize = 124;

        const itemData = json("inventory").slice();

        let c = 0;
        for(let i in itemData){
            //itemData[i].amount = c > 2 ? (10 + Math.floor(Math.random() * 10)) : 0;//this._inventory.itemQuantity(itemData[i].id);

            if (itemData[i].phase > settings.PHASE) {
                continue;
            }
     
            itemData[i].amount = this._inventory.itemQuantity(itemData[i].id);

            c++;
        }

        itemData.sort((a, b) => {
            return b.amount - a.amount;
        });
        
        const areaWidth = this._panel.width * 0.89;
        const areaHeight = this._panel.height * 0.90;
        const gridWidth = 5;
        const gridHeight = 4;
        const margin = (areaWidth - (boxSize * gridWidth)) / (gridWidth-1); 

        const colorMatrix = new PIXI.filters.ColorMatrixFilter();
        colorMatrix.desaturate();

        let currentX = 0;
        let currentY = 0;

        for(let i = 0; i < itemData.length; i++){
            var item = new PIXI.Sprite(texture('inventory_slot_on'));

            if (itemData[i].phase > settings.PHASE) {
                continue;
            }
            
            this._items.push(item);
            this._itemHolder.addChild(item);
            item.width = item.height = boxSize;
            item.x = -(areaWidth/2) + (currentX * (boxSize)) + (currentX * margin);
            item.y = -(areaHeight/2) + (currentY * (boxSize)) + (currentY * margin);
            currentX ++;
            if(currentX >= gridWidth){
                currentY ++;
                currentX = 0;
            }
            item.icon = new PIXI.Sprite(texture(itemData[i].thumbnail));
            item.icon.anchor.set(0.5);
            //item.icon.scale.set(0.5);
            item.icon.x = boxSize/2;
            item.icon.y = boxSize/2;
            item.addChild(item.icon);
            //console.log(item.icon);

            const wScale = item.width / item.icon.width;
            const hScale = item.height / item.icon.height;

			//if(wScale != null && hScale != null) {
				item.icon.scale.set(Math.min(wScale, hScale));
			//}

            if (itemData[i].amount == 0) {
                item.icon.alpha = 0.5;
                item.icon.filters = [colorMatrix];
                item.texture = texture('inventory_slot_off');
            }
            else {
                item.amountTag = new PIXI.Sprite(texture("inventory_slot_amount"));
                item.addChild(item.amountTag);

                const tagSize = boxSize*0.35;

                item.amountLabel = new PIXI.BitmapText(String(itemData[i].amount), {
                    fontName: "Alghera Pro",
                    fontSize: 30,
                    align: "left",
                    tint: 0xFFFFFF
                });
                item.amountLabel.position.set(boxSize - (tagSize*0.5),(tagSize*0.5));
                item.amountLabel.anchor.set(0.5);
                item.addChild(item.amountLabel);
        
                resizeLabelToFitText(item.amountLabel, tagSize*0.7);
            }
        }

        this._scrollMax = Math.max(0, currentY - gridHeight);
        this._scrollDistance = boxSize + margin;

        const mask = new PIXI.Sprite(PIXI.Texture.WHITE);
        this._panel.addChild(mask);
        mask.anchor.set(0.5);
        mask.width = areaWidth;
        mask.height = areaHeight;
        this._itemHolder.mask = mask;

        if(this._items.length < gridWidth * gridHeight){
            this._upButton.visible = this._upButton.interactive = this._downButton.visible = this._downButton.interactive = false;
        }
    }

    animateShow()
    {
        this.animateTitleBannerIn();

        GameScene.active.addTween(gsap.to(this._bag, 0.3, {y:this._bag.y, ease:Expo.easeOut}));
        this._bag.y = common.STAGE_HEIGHT+this._bag.height;

        this._closeButton.scale.set(0);
        this._closeButton.interactive = false;

        GameScene.active.addTween(gsap.to(this._panel, 0.7, {delay:0.2, y:this._panel.y, ease:Expo.easeOut, onComplete:() => {
            this.animateCloseButtonIn();
        }}));
        this._panel.y = common.STAGE_HEIGHT + this._panel.height/2;

        this._coinHolder.scale.set(0);
        GameScene.active.addTween(gsap.to(this._coinHolder.scale, 0.3, {x:1, y:1, ease:Expo.easeOut}));
    }

    animateHide()
    {
        this.animateFillOut();
        this.animateTitleBannerOut();

        GameScene.active.addTween(gsap.to(this._bag, 0.2, {delay:0.3, y:common.STAGE_HEIGHT+this._bag.height, ease:Sine.easeIn}));

        this.animateCloseButtonOut();

        GameScene.active.addTween(gsap.to(this._panel, 0.5, {y:common.STAGE_HEIGHT + this._panel.height/2, ease:Sine.easeIn, onComplete:() => {
            this.signals.previous.dispatch();
        }}));

        GameScene.active.addTween(gsap.to(this._coinHolder.scale, 0.3, {x:0, y:0, ease:Expo.easeIn}));
    }

    scroll(direction)
    {
        if(this._scrolling)
            return;

        if(Math.sign(direction) > 0 && this._scrollPos >= this._scrollMax){
            return;
        }
        if(Math.sign(direction) < 0 && this._scrollPos <= 0){
            return;
        }

        this._scrolling = true;

        this._scrollPos += direction;

        for(let i = 0; i < this._items.length; i++)
        {
            GameScene.active.addTween(gsap.to(this._items[i], 0.5, {y:this._items[i].y+(this._scrollDistance*-direction), ease:Expo.easeOut, onComplete:() => {
                if(i == 0)
                    this._scrolling = false;
            }}));
        }
    }

    destroy() {
        this._closeButton.destroy();

        P3.audio.stopMusic();

        super.destroy();
    }

    update() {
        super.update();
    }

    /**
     * @return {Inventory}
     */
    get inventory() {
        return this._inventory;
    }
}

/**
 * @static
 * @type {InventoryScene}
 */
InventoryScene.active = null;